<template>
  <div class="login-outer">
    <div class="login-card">
      <!-- 左侧：品牌 + 插画（文字紧贴插画上方） -->
      <div class="left-panel">
        <div class="left-content">
          <div class="brand-section">
            <h1 class="brand-title">开贝萌果分销后台</h1>
            <p class="brand-desc">打造独立自主的流量变现王国</p>
          </div>
          <div class="illustration-wrap">
            <img
              src="../assets/login-illustration.png"
              alt=""
              class="illustration-img"
            >
          </div>
        </div>
      </div>

      <!-- 右侧：登录表单 -->
      <div class="right-panel">
        <div class="login-wrapper">
          <h2 class="welcome-title">欢迎回来</h2>

          <el-form
            ref="loginForm"
            :model="loginForm"
            :rules="loginRules"
            label-width="0px"
            class="login-form"
          >
            <el-form-item prop="username">
              <el-input
                v-model="loginForm.username"
                placeholder="请输入账号"
                clearable
                @keyup.enter.native="handleLogin"
              />
            </el-form-item>
            <el-form-item prop="password">
              <el-input
                v-model="loginForm.password"
                type="password"
                placeholder="请输入密码"
                show-password
                @keyup.enter.native="handleLogin"
              />
            </el-form-item>

            <div class="form-extra">
              <el-checkbox v-model="rememberMe">记住我</el-checkbox>
            </div>

            <el-form-item class="login-btn-item">
              <el-button
                type="primary"
                class="login-btn"
                :loading="loading"
                @click="handleLogin"
              >
                立即登录
              </el-button>
            </el-form-item>
          </el-form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * 解码 JWT token 的 payload 部分（不验证签名）
 * @param {string} token - JWT token 字符串
 * @returns {object|null} 解码后的 payload 对象
 */
function parseJwtPayload(token) {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(json)
  } catch (e) {
    return null
  }
}

export default {
  name: 'LoginPage',
  data() {
    return {
      loginForm: {
        username: '',
        password: ''
      },
      loginRules: {
        username: [
          { required: true, message: '请输入用户名', trigger: 'blur' }
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' }
        ]
      },
      loading: false,
      rememberMe: true
    }
  },
  methods: {
    async handleLogin() {
      if (!this.rememberMe) {
        this.$message.warning('请勾选「记住我」后再登录')
        return
      }
      this.$refs.loginForm.validate(async valid => {
        if (valid) {
          this.loading = true
          try {
            const response = await fetch('/admin/login', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                username: this.loginForm.username,
                password: this.loginForm.password
              })
            })

            const result = await response.json()

            if (result.code === 200 && result.data && result.data.token) {
              const token = result.data.token
              localStorage.setItem('token', token)

              const payload = parseJwtPayload(token)
              if (!payload) {
                localStorage.removeItem('token')
                this.$message.error('解析登录信息失败')
                this.loading = false
                return
              }

              if (payload.status === 1) {
                localStorage.removeItem('token')
                this.$message.error('该账号已被禁用，请联系管理员')
                this.loading = false
                return
              }

              const roleVal = parseInt(payload.role, 10)
              const statusVal = parseInt(payload.status, 10)

              if (roleVal !== 0 && roleVal !== 1) {
                localStorage.removeItem('token')
                this.$message.error('账号角色无效，请联系管理员')
                this.loading = false
                return
              }

              localStorage.setItem('role', roleVal)
              localStorage.setItem('status', !isNaN(statusVal) ? statusVal : 0)
              localStorage.setItem('username', payload.username || this.loginForm.username)
              const userId = payload.adminid || payload.adminId || result.data.id || result.data.userId || payload.id || payload.userId || payload.sub || ''
              localStorage.setItem('userId', userId)

              if (this.rememberMe) {
                localStorage.setItem('rememberedUsername', this.loginForm.username)
              } else {
                localStorage.removeItem('rememberedUsername')
              }

              this.$router.push('/ad-revenue')
              this.$message.success('登录成功')
            } else {
              this.$message.error(result.msg || '登录失败：未返回有效数据')
            }
          } catch (error) {
            this.$message.error(error.message || '登录失败')
          } finally {
            this.loading = false
          }
        }
      })
    },
  },
  mounted() {
    const rememberedUsername = localStorage.getItem('rememberedUsername')
    if (rememberedUsername) {
      this.loginForm.username = rememberedUsername
      this.rememberMe = true
    }
  }
}
</script>

<style scoped>
.login-outer {
  --login-blue: #448aff;
  --login-blue-dark: #2f74f0;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  box-sizing: border-box;
}

.login-card {
  display: flex;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: #fff;
}

/* ===== 左侧 ===== */
.left-panel {
  flex: 1.05;
  background: var(--login-blue);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  padding: clamp(32px, 5vh, 56px) clamp(28px, 4vw, 48px);
}

/* 文字 + 插画作为一组，高度随内容，不撑满整列 */
.left-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: auto;
  max-width: 480px;
  height: auto;
}

.brand-section {
  position: relative;
  z-index: 2;
  flex-shrink: 0;
  width: 100%;
  padding: 0 0 20px;
  color: #fff;
  text-align: left;
}

.brand-title {
  margin: 0 0 10px;
  font-size: clamp(28px, 3.2vw, 40px);
  font-weight: 700;
  letter-spacing: 3px;
  line-height: 1.3;
}

.brand-desc {
  margin: 0;
  font-size: clamp(14px, 1.2vw, 17px);
  opacity: 0.92;
  letter-spacing: 1px;
  line-height: 1.6;
}

/* 插画区域随图片缩小，紧贴文字下方 */
.illustration-wrap {
  display: block;
  position: relative;
  z-index: 1;
  width: 100%;
  line-height: 0;
}

.illustration-img {
  width: 100%;
  max-width: 450px;
  height: auto;
  display: block;
  user-select: none;
  pointer-events: none;
}

/* ===== 右侧 ===== */
.right-panel {
  flex: 1;
  background: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: clamp(32px, 6vh, 64px) clamp(32px, 5vw, 80px);
  min-width: 0;
}

.login-wrapper {
  width: 100%;
  max-width: min(400px, 100%);
}

.welcome-title {
  margin: 0 0 clamp(28px, 5vh, 48px);
  text-align: center;
  font-size: clamp(22px, 2vw, 28px);
  font-weight: 600;
  color: #374151;
  letter-spacing: 1px;
}

.login-form >>> .el-form-item {
  margin-bottom: clamp(18px, 2.5vh, 28px);
}

.login-form >>> .el-input__inner {
  height: clamp(44px, 5.5vh, 52px);
  line-height: clamp(44px, 5.5vh, 52px);
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: #fff;
  padding: 0 20px;
  font-size: clamp(14px, 1.1vw, 16px);
  color: #374151;
  transition: border-color 0.2s;
}

.login-form >>> .el-input__inner:focus {
  border-color: var(--login-blue);
}

.login-form >>> .el-input__inner::placeholder {
  color: #9ca3af;
}

.login-form >>> .el-input__suffix {
  right: 14px;
}

.login-form >>> .el-input__clear,
.login-form >>> .el-input__password {
  color: #9ca3af;
}

.form-extra {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 0 clamp(20px, 3vh, 28px);
  padding: 0 6px;
}

.form-extra >>> .el-checkbox__label {
  font-size: clamp(13px, 1vw, 15px);
  color: #6b7280;
  padding-left: 6px;
}

.form-extra >>> .el-checkbox__input.is-checked .el-checkbox__inner {
  background-color: var(--login-blue);
  border-color: var(--login-blue);
}

.form-extra >>> .el-checkbox__inner:hover {
  border-color: var(--login-blue);
}

.login-btn-item {
  margin-bottom: 0;
}

.login-btn {
  width: 100%;
  height: clamp(44px, 5.5vh, 52px);
  border-radius: 999px !important;
  font-size: clamp(15px, 1.2vw, 17px);
  font-weight: 600;
  letter-spacing: 2px;
  background: var(--login-blue) !important;
  border: none !important;
  box-shadow: 0 6px 16px rgba(68, 138, 255, 0.35);
  transition: background 0.2s, box-shadow 0.2s, transform 0.15s;
}

.login-btn:hover,
.login-btn:focus {
  background: var(--login-blue-dark) !important;
  box-shadow: 0 8px 20px rgba(47, 116, 240, 0.4) !important;
  transform: translateY(-1px);
}

/* ===== 响应式 ===== */
@media screen and (max-width: 900px) {
  .login-card {
    flex-direction: column;
  }

  .left-panel {
    flex: none;
    height: 36vh;
    min-height: 200px;
    padding: 20px 16px 12px;
  }

  .left-content {
    max-width: 340px;
  }

  .brand-section {
    padding: 0 0 12px;
  }

  .brand-title {
    font-size: 24px;
  }

  .brand-desc {
    font-size: 13px;
  }

  .illustration-img {
    max-width: 280px;
  }

  .right-panel {
    flex: 1;
    padding: 36px 28px;
  }
}

@media screen and (max-width: 480px) {
  .left-panel {
    height: 30vh;
    min-height: 160px;
    padding: 16px 12px 8px;
  }

  .left-content {
    max-width: 260px;
  }

  .brand-section {
    padding: 0 0 8px;
  }

  .illustration-img {
    max-width: 200px;
  }

  .right-panel {
    padding: 28px 20px;
    align-items: flex-start;
  }

  .login-wrapper {
    max-width: 100%;
  }
}
</style>
